import loopback from './loopback'

const UNBOUNDED_MINUTES = 999999

// O Suporte saiu da tela em mai/2026 e não volta com o IA4COM. O gêmeo com IA4COM dele
// tem código próprio no billing e passaria por este filtro se não estivesse listado aqui.
const EXCLUDED_PLAN_CODES = ['support']

const NO_CALLS_FEATURE = 'nao-realiza-ligacoes'

// Marca do produto IA4COM na oferta. Mesmo nome da flag de rollout que libera o menu
// (`services/rollout.js`), porque são o mesmo produto visto de dois lados.
//
// NÃO confundir com `offer.hasAi`: aquele é a IA de transcrição/resumo de ligação, que
// vários planos atuais (Negociação, Ilimitado) já têm. Filtrar plano por `hasAi` esconde
// planos da base, não os planos novos.
export const IA4COM_FEATURE_NAME = 'ia4com'

function getThresholdFromOffer (offer) {
  if (!offer || !Array.isArray(offer.thresholds) || offer.thresholds.length === 0) return null
  const firstEntry = offer.thresholds[0]
  if (firstEntry == null || firstEntry.threshold == null) return null
  return firstEntry.threshold
}

function doesNotMakeCalls (offer) {
  const features = Array.isArray(offer && offer.features) ? offer.features : []
  return features.some(feature => feature && feature.name === NO_CALLS_FEATURE)
}

// Teto de minutos do plano, única fonte para as faixas e para o rótulo de
// limite. `thresholds` vazio é ambíguo no billing: no Ilimitado significa "sem
// limite", no Gerencial significa "não faz ligação". Sem olhar a feature
// `nao-realiza-ligacoes` os dois viravam ilimitado, empatavam na ordenação e a
// faixa do Ilimitado saía invertida — nunca casando com ninguém.
function getMinutesCeiling (offer) {
  if (doesNotMakeCalls(offer)) return 0
  const threshold = getThresholdFromOffer(offer)
  if (threshold == null) return UNBOUNDED_MINUTES
  return Math.min(Number(threshold), UNBOUNDED_MINUTES)
}

function getMonthlyFee (offer) {
  return offer && offer.value != null ? Number(offer.value) : 0
}

// Preço "de" (riscado) definido no catálogo do billing, por oferta. `null` quando a
// oferta não tem.
function getOriginalFee (offer) {
  return offer && offer.originalValue != null ? Number(offer.originalValue) : null
}

// Oferta de tabela restrita a contas novas (campanha, ex.: Black November). O billing só
// devolve essas ofertas para conta elegível, então é o sinal de que a conta tem direito
// ao banner e aos selos da campanha.
function isCampaignOffer (offer) {
  return !!(offer.pricingTable && offer.pricingTable.newAccountsOnly === true)
}

function getPriceTableName (offer) {
  const name = offer.pricingTable && offer.pricingTable.name
  return typeof name === 'string' && name.trim() !== '' ? name.trim() : null
}

function buildMinutesRangeByPlanCode (offersSortedByCeiling) {
  const minutesRangeByPlanCode = {}
  let previousMaxMinutes = -1
  let previousCeiling = null
  let previousRange = null

  for (const offer of offersSortedByCeiling) {
    const planCode = offer.plan ? offer.plan.name : null
    if (planCode == null) continue

    const maxMinutes = getMinutesCeiling(offer)

    // Dois planos com o mesmo teto não são distinguíveis por consumo:
    // compartilham a faixa em vez de gerar uma faixa vazia para o segundo.
    if (maxMinutes === previousCeiling) {
      minutesRangeByPlanCode[planCode] = previousRange.slice()
      continue
    }

    previousRange = [previousMaxMinutes + 1, maxMinutes]
    minutesRangeByPlanCode[planCode] = previousRange
    previousMaxMinutes = maxMinutes
    previousCeiling = maxMinutes
  }

  return minutesRangeByPlanCode
}

function hasValidPlan (offer) {
  return offer && offer.active && offer.plan && offer.plan.name && !EXCLUDED_PLAN_CODES.includes(offer.plan.name)
}

function hasIa4com (offer) {
  const features = Array.isArray(offer && offer.features) ? offer.features : []
  return features.some(feature => feature && feature.name === IA4COM_FEATURE_NAME)
}

/**
 * Planos que a sugestão por consumo pode propor.
 *
 * Um plano e seu gêmeo com IA4COM têm o MESMO teto de minutos, então dividiriam a mesma
 * faixa e o mesmo usuário cairia nos dois grupos — aparecendo duas vezes na tela. A
 * sugestão olha só os planos sem IA4COM; a variante com IA4COM é escolha explícita do
 * cliente.
 */
export function getSuggestionTiers (pricingList) {
  if (!Array.isArray(pricingList)) return []
  return pricingList.filter(plan => plan.hasIa4com !== true)
}

// Catálogo de ofertas da conta logada, pelo pbxapi (que repassa ao billing com a conta
// da sessão; o portal não fala mais com o billing nem tem a chave dele):
// - sem `purpose`: a tabela em que a conta vive, a mesma que o billing usa para cobrar
//   a vaga nova e a pró-rata dela. Conta sem assinatura ativa recebe o catálogo de quem
//   contrata hoje: a vigente, ou a campanha (Black November) se a conta for elegível;
// - com `purpose: 'contract'` (tela de contratação e checkout): sempre o catálogo de
//   quem contrata hoje, ignorando a tabela antiga da conta. Conta suspensa pela régua
//   chega à contratação com a assinatura ainda ativa e recontrataria no preço da geração
//   antiga dela. O checkout grava o `priceTableName` da oferta na assinatura, então
//   cards e checkout precisam pedir com o mesmo `purpose`.
export function getOffers ({ purpose } = {}) {
  return loopback.get('/subscriptions/offers', { params: purpose ? { purpose } : undefined })
}

export function loadAndMapOffers ({ purpose } = {}) {
  return getOffers({ purpose })
    .then(mapOffersToPricingList)
    .catch(() => [])
}

export function mapOffersToPricingList (offers) {
  if (!Array.isArray(offers)) return []
  if (offers.length === 0) return []

  const offersWithPlan = offers.filter(hasValidPlan)

  const offersSortedByCeiling = [...offersWithPlan].sort((offerA, offerB) => {
    const ceilingDifference = getMinutesCeiling(offerA) - getMinutesCeiling(offerB)
    // Empate de teto resolvido pela mensalidade, para a ordem não depender de
    // como o billing devolveu as ofertas.
    if (ceilingDifference !== 0) return ceilingDifference
    return getMonthlyFee(offerA) - getMonthlyFee(offerB)
  })

  // Só os planos sem IA4COM entram nas faixas: são eles que a sugestão por consumo propõe.
  const minutesRangeByPlanCode = buildMinutesRangeByPlanCode(offersSortedByCeiling.filter(offer => !hasIa4com(offer)))

  const pricingList = offersSortedByCeiling.map(offer => {
    const plan = offer.plan
    const planCode = plan.name
    const monthlyFee = getMonthlyFee(offer)
    const features = Array.isArray(offer.features) ? offer.features : []
    const minutesCeiling = getMinutesCeiling(offer)
    // 0 = não realiza ligações · null = ilimitado
    const minutes = minutesCeiling >= UNBOUNDED_MINUTES ? null : minutesCeiling
    // `null` nos planos com IA4COM: eles não são candidatos da sugestão, e uma faixa
    // inventada os faria disputar usuários com o gêmeo sem IA4COM.
    const minutesRange = minutesRangeByPlanCode[planCode] !== undefined
      ? minutesRangeByPlanCode[planCode]
      : null
    const displayName = plan.title != null ? plan.title : plan.name

    return {
      code: planCode,
      name: displayName,
      monthlyFee,
      originalFee: getOriginalFee(offer),
      // Tabela de onde saiu o preço do card: o checkout confere que vai gravar a mesma.
      priceTableName: getPriceTableName(offer),
      isCampaign: isCampaignOffer(offer),
      // Destaque do card (selo e quadro da campanha), marcado por oferta no billing.
      highlight: offer.highlight === true,
      color: offer.color || '#374151',
      minutes,
      minutesRange,
      recommendedFor: plan.recommendedFor,
      features,
      // IA de transcrição/resumo — produto antigo, já presente em planos atuais.
      hasAi: offer.hasAi === true,
      // Produto IA4COM — o que a TEL-2295 introduz.
      hasIa4com: hasIa4com(offer)
    }
  })

  // Primeiro os planos sem IA4COM, depois os com — cada bloco por mensalidade. Ordenar só
  // por preço intercalaria os dois (o Gerencial + IA4COM cai entre Negociação e Ilimitado).
  // Como os dois blocos ficam na mesma ordem relativa, no grid de 3 por linha cada plano
  // base fica exatamente acima do seu gêmeo.
  return pricingList.sort((itemA, itemB) => {
    if (itemA.hasIa4com !== itemB.hasIa4com) return itemA.hasIa4com ? 1 : -1
    return itemA.monthlyFee - itemB.monthlyFee
  })
}



// WEBPACK FOOTER //
// ./src/services/pricing-offers.js